import { useState, useEffect, useContext, useMemo, useRef } from "react";
import {
  Grid,
  Typography,
  useTheme,
  SvgIcon,
  Box,
  Stack,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
} from "@mui/material";
import { AppContext } from "../contextProviders/AppContext";
import { centsToLocalString } from "../shared/money";
import AdditionalFieldsDialog from "./AdditionalFieldsDialog";
import { replaceVolumeIcon, replaceWorkIcon } from "../features";
import { translation } from "./Dictionary";
import { useGetImage } from "./customHooks";
import ProductImage from "./ProductImage";

// Custom hook to determine when images have been loaded
const useImageLoaded = () => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef();

  const onLoad = () => {
    setLoaded(true);
  };

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      onLoad();
    }
  });

  return [imgRef, loaded, onLoad];
};

function InfoLabel(props) {
  return <Typography variant="caption" {...props} />;
}
function ExtraField({ unit, icon, theme, value }) {
  return (
    <Grid
      container
      item
      wrap="nowrap"
      direction="row"
      style={{ marginLeft: "10px", width: "unset" }}
    >
      <div style={{ fontSize: 20, color: theme.palette.primary.extraFields }}>
        {icon}
      </div>
      <Typography
        variant="caption"
        style={{
          marginLeft: 5,
          fontSize: 13,
          color: theme.palette.primary.text,
          fontWeight: 600,
        }}
      >
        {value}
      </Typography>
    </Grid>
  );
}

function WeightIcon(props) {
  return (
    <SvgIcon viewBox="0 0 8800 10000" {...props}>
      <path d="M1609 3590l1054 0c-306,-379 -490,-861 -490,-1385 0,-1218 988,-2205 2205,-2205 1218,0 2205,987 2205,2205 0,524 -183,1006 -489,1385l1089 0c669,0 1172,549 1217,1217l300 4462c45,668 -548,1217 -1217,1217l-6262 0c-669,0 -1275,-550 -1217,-1217l388 -4462c58,-667 548,-1217 1217,-1217zm1620 3462l936 -1394 -764 0 -731 1136 0 -1136 -642 0 0 2815 642 0 0 -1103 829 1103 821 0 -1091 -1421zm2529 1470c752,0 1344,-478 1344,-1544l-1491 0 0 519 793 0c0,278 -348,453 -662,453 -482,0 -821,-420 -821,-882 0,-470 347,-862 800,-862 241,0 454,106 605,282l474 -396c-245,-295 -633,-483 -1079,-483 -813,0 -1446,658 -1446,1459 0,792 617,1454 1483,1454zm-1380 -7543c677,0 1226,549 1226,1226 0,677 -549,1226 -1226,1226 -677,0 -1226,-549 -1226,-1226 0,-677 549,-1226 1226,-1226z" />
    </SvgIcon>
  );
}

export default function SelectedProductCard({ offer, imageSize }) {
  const [imgRef, loaded, onLoad] = useImageLoaded();
  const theme = useTheme();

  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  const {
    selectedCompany,
    language,
    mobileViewPort,
    FEATURE_FIXED_RESERVATION_DURATION,
  } = useContext(AppContext);

  const FEATURE_REPLACE_VOLUME_ICON = useMemo(() => {
    return true;
    return replaceVolumeIcon(process.env.REACT_APP_ENV, selectedCompany);
  }, [selectedCompany]);

  const image = useGetImage(offer.vehicle?.image);
  const FEATURE_REPLACE_WORK_ICON = useMemo(() => {
    return replaceWorkIcon(process.env.REACT_APP_ENV, selectedCompany);
  }, [selectedCompany]);

  const additionalFields = offer.vehicle.additionalFields;

  const customFields = useMemo(() => {
    if (!additionalFields || additionalFields?.length === 0) return [];
    if (additionalFields) {
      const knownFields = {
        passengers: additionalFields.find((f) => f.key === "passengers"),
        requiredLicense: additionalFields.find(
          (f) => f.key === "requiredLicense"
        ),
        volume: additionalFields.find((f) => f.key === "volume"),
      };
      const _knownFieldNames = Object.keys(knownFields);

      return additionalFields.filter((f) => !_knownFieldNames.includes(f.key));
    }
  }, [additionalFields]);

  return (
    <>
      <ListItem>
        <ListItemAvatar>
          <Avatar
            variant="rounded"
            sx={{
              height: imageSize,
              width: imageSize,
              bgcolor: "transparent",
            }}
          >
            <ProductImage offer={offer} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={
            <Stack>
              <Typography
                sx={{
                  fontSize: "16px",
                }}
              >
                {offer?.vehicle?.name}
              </Typography>
              {offer.availableInCategory &&
                !FEATURE_FIXED_RESERVATION_DURATION && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.primary.mainText,
                      fontSize: "13px",
                    }}
                  >
                    {translation.get(language, "Or equivalent text")}{" "}
                    {offer.vehicle.category.name}{" "}
                    {translation.get(language, "product text")}
                  </Typography>
                )}
            </Stack>
          }
          secondary={
            customFields.length > 0 ? (
              <Box onClick={() => setShowAdditionalFields(true)}>
                <Typography
                  sx={{
                    color: theme.palette.primary.additionalInfo,
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "13px",
                  }}
                >
                  {translation.get(language, "More information")}
                </Typography>
              </Box>
            ) : null
          }
        />
        <ListItemSecondaryAction>
          <Typography sx={{ color: theme.palette.primary.mainText }}>
            {centsToLocalString(offer.offerPrice, true)}
          </Typography>
        </ListItemSecondaryAction>
      </ListItem>
      {showAdditionalFields && (
        <AdditionalFieldsDialog
          open={showAdditionalFields}
          offer={offer}
          onClose={() => setShowAdditionalFields(false)}
        />
      )}
    </>
  );
}
